/**
 * BlankLegalTemplatesCard — the Blank-legal-templates box (#3, Pedro 2026-09-13),
 * shown on its own page reached from the Welcome dashboard tile. Presents the
 * firm's UNFILLED legal documents the way Agreement → Documents does: a
 * select-then-download grid with PT / EN columns, each offering Word / PDF via
 * checkboxes (unavailable formats greyed). No On-file/Fillable columns and no
 * Client/AML/Bank rows — those don't apply to blank templates.
 *
 * These carry NO client data: the IAA prints with empty placeholders; Annex I
 * shows the Standard fee schedule; the ADQC is the filled-shape questionnaire
 * with every field blank; P&P / Sample MR are the firm's generic documents.
 *
 * Fee simulation (Pedro 2026-10-10): when the selection includes an Annex I PDF, Download
 * first asks "Include a fee simulation in Annex I?". No → the standard Annex I as before.
 * Yes → the fee calculator opens in a dialog; Continue downloads the Annex I PDF(s) for the
 * chosen schedule with the worked example (`downloadFeeExamplePdf`), the rest unchanged.
 * The Word Annex I always stays the standard template (the example exists as PDF only).
 */
import { Button, Card, Checkbox, Group, Modal, Stack, Table, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconCalculator, IconDownload, IconFileText } from '@tabler/icons-react';
import { useState } from 'react';

import type { DocumentLanguage } from '../api/types';
import {
  downloadBlob, downloadFeeExamplePdf, downloadLegalTemplate,
  type LegalTemplateFormat, type LegalTemplateKind,
} from '../api/legalTemplates';
import { FeeCalculator, useFeeExample, type FeeCalcValue } from './FeeCalculatorCard';

export interface DocRow {
  kind: LegalTemplateKind;
  name: string;
  note?: string;
  word: boolean;
  pdf: boolean;
  /** Languages this document exists in (default: both). A missing one shows greyed with the reason (§3h). */
  langs?: DocumentLanguage[];
}

// Available formats mirror the Agreement → Documents list. Order: the blank client-record
// forms first (DF1, Pedro 2026-10-12 — the first document a prospect fills), then the IAA
// and its annexes. Exported: the record Email (P34) offers the same rows and formats.
export const DOCS: DocRow[] = [
  // P16 (team feedback 2026-10-09): the blank client-record forms ("Ficha de cliente"),
  // generated by the app in both languages since P16b step (c).
  { kind: 'clientform_individual', name: 'Client record — individual', note: 'Fillable PDF', word: false, pdf: true },
  { kind: 'clientform_legal', name: 'Client record — legal person', note: 'Fillable PDF', word: false, pdf: true },
  { kind: 'iaa', name: 'Investment Advisory Agreement (IAA)', word: true, pdf: false },
  { kind: 'fees', name: 'Annex I — Fees', note: 'Standard fee schedule', word: true, pdf: true },
  { kind: 'aq', name: 'Annex II — Adequacy questionnaire', note: 'Blank — no answers', word: true, pdf: false },
  { kind: 'pp', name: 'Annex III — Policies & procedures', word: true, pdf: true },
  { kind: 'samplemonthly', name: 'Annex IV — Sample monthly report', word: false, pdf: true },
];

const LANGS: { col: DocumentLanguage; label: string }[] = [
  { col: 'PT', label: 'Portuguese' },
  { col: 'EN', label: 'English' },
];
const FMTS: LegalTemplateFormat[] = ['word', 'pdf'];
const FMT_LABEL: Record<LegalTemplateFormat, string> = { word: 'Word', pdf: 'PDF' };

const keyOf = (kind: string, lang: DocumentLanguage, fmt: LegalTemplateFormat) => `${kind}:${lang}:${fmt}`;
const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function BlankLegalTemplatesCard({ calc, onCalc }: {
  /** The fee calculator's inputs, shared with the page's calculator card. */
  calc: FeeCalcValue;
  onCalc: (v: FeeCalcValue) => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [askSimulation, setAskSimulation] = useState(false);
  const [calcOpen, setCalcOpen] = useState(false);
  // Continue needs a worked example for the current inputs (not a stale placeholder).
  const exampleQ = useFeeExample(calc);
  const simulationReady = !!calc.scheduleId && calc.amount !== null && !!exampleQ.data
    && !exampleQ.error && !exampleQ.isPlaceholderData;

  const annexPdfSelected = [...selected].some((k) => k.startsWith('fees:') && k.endsWith(':pdf'));
  const annexWordSelected = [...selected].some((k) => k.startsWith('fees:') && k.endsWith(':word'));

  function toggle(key: string) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  }

  function handleDownload() {
    if (selected.size === 0) return;
    if (annexPdfSelected) { setAskSimulation(true); return; }
    void runDownloads(null);
  }

  async function runDownloads(simulation: FeeCalcValue | null) {
    // Parse the selection back into (kind, lang, fmt) triples and download each,
    // spaced 400ms to dodge the browser multi-download gate (as the Documents tab).
    const items = [...selected].map((k) => {
      const [kind, lang, fmt] = k.split(':');
      return { kind: kind as LegalTemplateKind, lang: lang as DocumentLanguage, fmt: fmt as LegalTemplateFormat };
    });
    if (items.length === 0) return;
    setBusy(true);
    try {
      for (let i = 0; i < items.length; i += 1) {
        if (i > 0) await sleep(400);
        const { kind, lang, fmt } = items[i];
        try {
          const { blob, fileName } = simulation?.scheduleId && simulation.amount !== null
            && kind === 'fees' && fmt === 'pdf'
            ? await downloadFeeExamplePdf(simulation.scheduleId, simulation.amount, lang, simulation.frequency)
            : await downloadLegalTemplate(kind, lang, fmt);
          downloadBlob(blob, fileName);
        } catch (e) {
          notifications.show({
            color: 'red',
            title: 'Download failed',
            message: e instanceof Error ? e.message : 'A template could not be generated.',
          });
        }
      }
      setSelected(new Set());
    } finally {
      setBusy(false);
    }
  }

  function fmtBox(row: DocRow, lang: DocumentLanguage, fmt: LegalTemplateFormat) {
    const available = fmt === 'word' ? row.word : row.pdf;
    const key = keyOf(row.kind, lang, fmt);
    return (
      <Checkbox
        key={fmt}
        size="sm"
        disabled={!available || busy}
        checked={available && selected.has(key)}
        onChange={() => toggle(key)}
        label={<Text size="sm" c={available ? undefined : 'dimmed'}>{FMT_LABEL[fmt]}</Text>}
      />
    );
  }

  return (
    <Card withBorder padding="lg" radius="md">
      <Modal opened={askSimulation} onClose={() => setAskSimulation(false)} centered
        title={<Text fw={600}>Include a fee simulation?</Text>}>
        <Stack>
          <Text size="sm">Do you want to include a fee simulation in Annex I (PDF)?</Text>
          <Text size="xs" c="dimmed">
            A worked example of a fee schedule at an amount you choose, added under the
            schedule's terms.
            {annexWordSelected && ' The Word Annex I stays the standard template.'}
          </Text>
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setAskSimulation(false)}>Cancel</Button>
            <Button variant="default"
              onClick={() => { setAskSimulation(false); void runDownloads(null); }}>
              No
            </Button>
            <Button onClick={() => { setAskSimulation(false); setCalcOpen(true); }}>Yes</Button>
          </Group>
        </Stack>
      </Modal>

      <Modal opened={calcOpen} onClose={() => setCalcOpen(false)} centered size="xl"
        title={(
          <Group gap="xs">
            <IconCalculator size={18} />
            <Text fw={600}>Fee simulation for Annex I</Text>
          </Group>
        )}>
        <Stack>
          <FeeCalculator value={calc} onChange={onCalc} />
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setCalcOpen(false)}>Cancel</Button>
            <Button leftSection={<IconDownload size={16} />} disabled={!simulationReady}
              onClick={() => { setCalcOpen(false); void runDownloads(calc); }}>
              Continue
            </Button>
          </Group>
        </Stack>
      </Modal>

      <Stack gap="sm">
        <Group gap="xs">
          <IconFileText size={20} />
          <Title order={4}>Blank legal templates</Title>
        </Group>
        <Text size="sm" c="dimmed">
          Unfilled versions of each legal document — the shape of what a client may
          be asked to sign. Tick the artefacts you want, then Download. These carry
          no client data: the IAA prints with empty placeholders, Annex I shows the
          Standard fee schedule, the ADQC is a blank questionnaire, and the client
          records are the firm's blank fillable forms.
        </Text>

        <Group justify="flex-end">
          <Button
            leftSection={<IconDownload size={16} />}
            onClick={handleDownload}
            loading={busy}
            disabled={selected.size === 0}
          >
            Download ({selected.size})
          </Button>
        </Group>

        <Table verticalSpacing="sm" horizontalSpacing="md">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Document</Table.Th>
              {LANGS.map((l) => <Table.Th key={l.col}>{l.label}</Table.Th>)}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {DOCS.map((row) => (
              <Table.Tr key={row.kind}>
                <Table.Td>
                  <Text size="sm">{row.name}</Text>
                  {row.note && <Text size="xs" c="dimmed">{row.note}</Text>}
                </Table.Td>
                {LANGS.map((l) => (
                  <Table.Td key={l.col}>
                    {row.langs && !row.langs.includes(l.col) ? (
                      <Text size="xs" c="dimmed">No {l.label.toLowerCase()} version yet</Text>
                    ) : (
                      <Group gap="lg">
                        {FMTS.map((fmt) => fmtBox(row, l.col, fmt))}
                      </Group>
                    )}
                  </Table.Td>
                ))}
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Stack>
    </Card>
  );
}
